"use client";

import { TrashIcon } from "@heroicons/react/24/outline";
import { useState } from "react";

import { DeleteConversionModal } from "@/app/modals/DeleteConversionModal";
import { Button } from "@/components/buttons/Button";
import { LinkButton } from "@/components/links/LinkButton";
import { NormalText } from "@/components/text/NormalText";
import { SmallTitle } from "@/components/text/SmallTitle";
import { conversionNamespace } from "@/constants/namespaces";
import { useNamedSelectedArtist } from "@/lib/zustand/useSelectedArtist";
import {
  Artist,
  ConversionConstructedNoDates,
  Instrument,
  MixedModelConstructedNoDates,
  ModelNoDates,
} from "@/types/types";
import { createMixedModelName } from "@/utils/createMixedModelName";
import { formatDateForOutput } from "@/utils/formatTime/formatDateForOutput";
import { useIsClient } from "@/utils/hooks/useIsClient";

export function Details({
  conversion,
  artistInstrumentModelMixedModel,
}: {
  conversion: ConversionConstructedNoDates;
  artistInstrumentModelMixedModel:
    | Artist
    | Instrument
    | ModelNoDates
    | MixedModelConstructedNoDates
    | null;
}) {
  const isClient = useIsClient();
  const isMultiConversion = conversion.conversionMultiId !== null;

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // because this is displayed on the conversion page, we use the conversionNamespace namespace
  const useSelectedArtist = useNamedSelectedArtist((state) =>
    state.getNamedStore(conversionNamespace)
  );
  const setSelectedArtistId = useSelectedArtist(
    (state) => state.setSelectedArtistId
  );

  return (
    <div className="mt-2">
      <div className="flex justify-between gap-x-6">
        <SmallTitle elementType="span" className="whitespace-nowrap text-xs">
          Created:
        </SmallTitle>
        <NormalText elementType="span" className="break-all text-xs">
          {isClient &&
            formatDateForOutput({
              date: new Date(conversion.createdAtString),
              withTime: true,
              withYear: false,
            })}
        </NormalText>
      </div>

      {artistInstrumentModelMixedModel !== null && (
        <div className="mt-2 flex justify-between gap-x-6">
          <SmallTitle elementType="span" className="whitespace-nowrap text-xs">
            Voice:
          </SmallTitle>
          <NormalText elementType="span" className="break-all text-xs">
            <LinkButton
              onClick={() => setSelectedArtistId(conversion.voiceId)}
              className="text-xs"
            >
              {"name" in artistInstrumentModelMixedModel ?
                artistInstrumentModelMixedModel.name
              : createMixedModelName(artistInstrumentModelMixedModel)}
            </LinkButton>
          </NormalText>
        </div>
      )}

      {conversion.pitchShift !== null && (
        <div className="mt-2 flex justify-between gap-x-6">
          <SmallTitle elementType="span" className="whitespace-nowrap text-xs">
            Pitch shift (semitones):
          </SmallTitle>
          <NormalText elementType="span" className="break-all text-xs">
            {conversion.pitchShift}
          </NormalText>
        </div>
      )}

      {conversion.conversionStrength !== null && (
        <div className="mt-2 flex justify-between gap-x-6">
          <SmallTitle elementType="span" className="whitespace-nowrap text-xs">
            Conversion strength:
          </SmallTitle>
          <NormalText elementType="span" className="break-all text-xs">
            {conversion.conversionStrength * 100}%
          </NormalText>
        </div>
      )}

      <div className="mt-2 flex justify-between gap-x-6">
        <SmallTitle elementType="span" className="whitespace-nowrap text-xs">
          Original file:
        </SmallTitle>
        <NormalText elementType="span" className="break-all text-xs">
          {conversion.name}
        </NormalText>
      </div>

      {conversion.removeInstrumental !== null && (
        <div className="mt-2 flex justify-between gap-x-6">
          <SmallTitle elementType="span" className="whitespace-nowrap text-xs">
            Remove instrumental:
          </SmallTitle>
          <NormalText elementType="span" className="break-all text-xs">
            {conversion.removeInstrumental ? "Yes" : "No"}
          </NormalText>
        </div>
      )}

      {conversion.removeBackgroundVocals !== null && (
        <div className="mt-2 flex justify-between gap-x-6">
          <SmallTitle elementType="span" className="whitespace-nowrap text-xs">
            Remove background vocals:
          </SmallTitle>
          <NormalText elementType="span" className="break-all text-xs">
            {conversion.removeBackgroundVocals ? "Yes" : "No"}
          </NormalText>
        </div>
      )}

      {conversion.deReverbDeEcho !== null && (
        <div className="mt-2 flex justify-between gap-x-6">
          <SmallTitle elementType="span" className="whitespace-nowrap text-xs">
            Remove reverb and echo:
          </SmallTitle>
          <NormalText elementType="span" className="break-all text-xs">
            {conversion.deReverbDeEcho ? "Yes" : "No"}
          </NormalText>
        </div>
      )}

      {conversion.deNoise !== null && (
        <div className="mt-2 flex justify-between gap-x-6">
          <SmallTitle elementType="span" className="whitespace-nowrap text-xs">
            Remove noise:
          </SmallTitle>
          <NormalText elementType="span" className="break-all text-xs">
            {conversion.deNoise ? "Yes" : "No"}
          </NormalText>
        </div>
      )}

      {(conversion.confirmedDone || conversion.failed) &&
        !isMultiConversion && (
          <>
            <div className="mt-4 flex justify-end">
              <Button
                variant="ghost"
                className="flex items-center gap-x-1 !p-0 text-xs"
                onClick={() => setIsDeleteModalOpen(true)}
              >
                Delete conversion
                <TrashIcon className="h-4 w-4" />
              </Button>
            </div>

            <DeleteConversionModal
              open={isDeleteModalOpen}
              closeModal={() => setIsDeleteModalOpen(false)}
              conversion={conversion}
            />
          </>
        )}
    </div>
  );
}
